import React, { useEffect, useMemo, useState } from "react";
import { FiSearch, FiRefreshCw, FiEye, FiPackage } from "react-icons/fi";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import adminAxios from "../../utils/adminAxios";
import ConsignmentApplicationDetails from "./consignmentApplicationDetails";

const STATUS_COLORS = {
  Initiated: "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200",
  Processed: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200",
  ExpertEdited:
    "bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200",
  Confirmed:
    "bg-cyan-100 text-cyan-800 dark:bg-cyan-900 dark:text-cyan-200",
  Shipped:
    "bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200",
  Received:
    "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200",
  ExpertReEdited:
    "bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200",
  Approved:
    "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200",
  Rejected: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200",
};

const ConsignmentApplications = () => {
  const { t } = useTranslation();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selected, setSelected] = useState(null);

  const statusLabels = useMemo(
    () => ({
      Initiated: t("dashboard.consignments.common.status.initiated"),
      Processed: t("dashboard.consignments.common.status.processed"),
      ExpertEdited: t("dashboard.consignments.common.status.expertEdited"),
      Confirmed: t("dashboard.consignments.common.status.confirmed"),
      Shipped: t("dashboard.consignments.common.status.shipped"),
      Received: t("dashboard.consignments.common.status.received"),
      ExpertReEdited: t(
        "dashboard.consignments.common.status.expertReEdited"
      ),
      Approved: t("dashboard.consignments.common.status.approved"),
      Rejected: t("dashboard.consignments.common.status.rejected"),
    }),
    [t]
  );

  const fetchApplications = async () => {
    try {
      setLoading(true);
      const response = await adminAxios.get("consignment-applications/all");
      setApplications(response.data.data || []);
    } catch (error) {
      toast.error(
        error.response?.data?.message ||
          t("dashboard.consignments.list.loadError")
      );
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchApplications();
  }, []);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchApplications();
  };

  const filteredApplications = useMemo(() => {
    return applications.filter((app) => {
      const matchesSearch =
        app.category?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        app.user?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        app.expert?.name?.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesStatus =
        statusFilter === "all" || app.status === statusFilter;

      return matchesSearch && matchesStatus;
    });
  }, [applications, searchTerm, statusFilter]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
            {t("dashboard.consignments.list.title")}
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            {t("dashboard.consignments.list.countLabel", {
              count: applications.length,
            })}
          </p>
        </div>
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          className="mt-4 lg:mt-0 flex items-center justify-center space-x-2 rtl:space-x-reverse px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
        >
          <FiRefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
          <span>{t("dashboard.consignments.list.refresh")}</span>
        </button>
      </div>

      {/* Search & Filter */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="relative flex-1 max-w-md">
          <FiSearch className="absolute left-3 rtl:left-auto rtl:right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder={t("dashboard.consignments.list.searchPlaceholder")}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 rtl:pl-4 rtl:pr-10 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
        >
          <option value="all">
            {t("dashboard.consignments.list.allStatuses")}
          </option>
          {Object.keys(statusLabels).map((status) => (
            <option key={status} value={status}>
              {statusLabels[status]}
            </option>
          ))}
        </select>
      </div>

      {/* Table */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-100 dark:border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-600">
                <th className="px-6 py-4 text-left rtl:text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t("dashboard.consignments.list.table.id")}
                </th>
                <th className="px-6 py-4 text-left rtl:text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t("dashboard.consignments.common.fields.category")}
                </th>
                <th className="px-6 py-4 text-left rtl:text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t("dashboard.consignments.common.fields.client")}
                </th>
                <th className="px-6 py-4 text-left rtl:text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t("dashboard.consignments.common.fields.expert")}
                </th>
                <th className="px-6 py-4 text-left rtl:text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t("dashboard.consignments.list.table.productsCount")}
                </th>
                <th className="px-6 py-4 text-left rtl:text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t("dashboard.consignments.list.table.status")}
                </th>
                <th className="px-6 py-4 text-left rtl:text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t("dashboard.consignments.common.fields.createdAt")}
                </th>
                <th className="px-6 py-4 text-left rtl:text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t("dashboard.consignments.list.table.actions")}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-600">
              {filteredApplications.map((app) => (
                <tr
                  key={app.id}
                  className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                >
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white text-left rtl:text-right">
                    #{app.id}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white text-left rtl:text-right">
                    {app.category?.name ||
                      t("dashboard.consignments.list.notSpecified")}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-left rtl:text-right">
                    <div className="text-sm font-medium text-gray-900 dark:text-white">
                      {app.user?.name ||
                        t("dashboard.consignments.list.notSpecified")}
                    </div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      {app.user?.number || ""}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-left rtl:text-right">
                    <div className="text-sm font-medium text-gray-900 dark:text-white">
                      {app.expert?.name ||
                        t("dashboard.consignments.list.notSpecified")}
                    </div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      {app.expert?.number || ""}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white text-left rtl:text-right">
                    {app.products?.length || 0}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-left rtl:text-right">
                    <span
                      className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        STATUS_COLORS[app.status] ||
                        "bg-gray-100 text-gray-800"
                      }`}
                    >
                      {statusLabels[app.status] || app.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white text-left rtl:text-right">
                    {new Date(app.createdAt).toLocaleDateString("ar-EG")}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-left rtl:text-right">
                    <button
                      onClick={() => setSelected(app)}
                      className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300 transition-colors px-3 py-1 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/20"
                    >
                      <FiEye className="w-4 h-4 inline ml-1 rtl:ml-0 rtl:mr-1" />
                      {t("dashboard.consignments.list.view")}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredApplications.length === 0 && (
          <div className="text-center py-12">
            <FiPackage className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              {t("dashboard.consignments.list.empty.title")}
            </h3>
            <p className="text-gray-500 dark:text-gray-400">
              {searchTerm || statusFilter !== "all"
                ? t("dashboard.consignments.list.empty.filteredSubtitle")
                : t("dashboard.consignments.list.empty.defaultSubtitle")}
            </p>
          </div>
        )}
      </div>

      <ConsignmentApplicationDetails
        application={selected}
        open={Boolean(selected)}
        onClose={() => setSelected(null)}
        statusLabels={statusLabels}
        statusColors={STATUS_COLORS}
      />
    </div>
  );
};

export default ConsignmentApplications;
